import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Autocomplete,
    Button,
    Chip,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { makeStyles } from '@mui/styles';
import { setSnackbar } from '../../../../../components/hooks/snackBar/reducers';
import Loader from '../../../../../components/loader/loader';
import { useStyle } from '../style';

// Scoped to this screen only — do not move into the shared reports/style.js
const useLocalStyle = makeStyles((theme) => ({
    tablrow1: {
        '&.MuiTableRow-root': {
            '&:nth-of-type(even)': {
                backgroundColor: theme.palette.grey[50]
            },
            '&:hover': {
                backgroundColor: '#f3e5f5'
            },
            '& .MuiTableCell-root': {
                textAlign: 'left',
                verticalAlign: 'middle',
                padding: '10px 12px',
                fontSize: '0.85rem',
                borderRight: `1px solid #eee`,
                whiteSpace: 'nowrap'
            },
            '& .MuiTableCell-root:first-of-type': {
                textAlign: 'center'
            }
        }
    },
    tblHeader: {
        '&.MuiTableHead-root': {
            '& .MuiTableCell-root': {
                fontWeight: 600,
                fontSize: '0.8rem',
                backgroundColor: '#ba68c8',
                paddingBlock: '10px',
                color: '#fff',
                letterSpacing: 0.5,
                whiteSpace: 'nowrap'
            }
        }
    }
}));

// Mirrors the event_type enum accepted by GET /v1/reports/barcode/audit_history
const EVENT_TYPE_OPTIONS = [
    { label: 'Purchase Receipt', value: 'PURCHASE_RECEIPT' },
    { label: 'Barcode Generated', value: 'BARCODE_GENERATED' },
    { label: 'Warehouse Entry', value: 'WAREHOUSE_ENTRY' },
    { label: 'Warehouse to Outlet Transfer', value: 'WAREHOUSE_TO_OUTLET_TRANSFER' },
    { label: 'Outlet to Outlet Transfer', value: 'OUTLET_TO_OUTLET_TRANSFER' },
    { label: 'Outlet to Warehouse Return', value: 'OUTLET_TO_WAREHOUSE_RETURN' },
    { label: 'Stock Verification', value: 'STOCK_VERIFIED' },
    { label: 'Missing', value: 'BARCODE_MARKED_MISSING' },
    { label: 'Reactivated', value: 'BARCODE_REACTIVATED' },
    { label: 'Sales', value: 'SALES' },
    { label: 'Sales Return', value: 'SALES_RETURN' },
    { label: 'Damaged', value: 'BARCODE_DAMAGED' },
    { label: 'Purchase Return', value: 'PURCHASE_RETURN' },
    { label: 'Deleted', value: 'BARCODE_DELETED' }
];

const EVENT_TYPE_LABELS = EVENT_TYPE_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {});

// Visual grouping so the timeline reads at a glance.
const EVENT_TYPE_COLORS = {
    BARCODE_GENERATED: '#9c27b0',            // Purple
    PURCHASE_RECEIPT: '#1976d2',             // Blue
    WAREHOUSE_ENTRY: '#757575',              // Gray
    WAREHOUSE_TO_OUTLET_TRANSFER: '#ed6c02',  // Orange
    OUTLET_TO_OUTLET_TRANSFER: '#ed6c02',    // Orange
    OUTLET_TO_WAREHOUSE_RETURN: '#ef6c00',   // Deep Orange
    SALES: '#00838f',                        // Teal
    SALES_RETURN: '#f9a825',                 // Amber
    STOCK_VERIFIED: '#388e3c',               // Green
    BARCODE_MARKED_MISSING: '#d32f2f',       // Red
    BARCODE_REACTIVATED: '#2e7d32',          // Green
    BARCODE_DAMAGED: '#b71c1c',              // Dark Red
    BARCODE_DELETED: '#b71c1c',              // Dark Red
    PURCHASE_RETURN: '#6d4c41'               // Brown
};

// Summary current_status comes from live barcode_list (not last event type).
const STATUS_COLORS = {
    Damaged: '#b71c1c',
    Missing: '#d32f2f',
    Sold: '#00838f',
    'Returned to Warehouse': '#6d4c41',
    'Purchase Returned': '#6d4c41',
    'At Outlet': '#ed6c02',
    'In Warehouse': '#757575',
    Deleted: '#b71c1c',
    Verified: '#388e3c',
    Reactivated: '#2e7d32',
    Transferred: '#ed6c02',
    Active: '#388e3c'
};

const DEFAULT_EVENT_COLOR = '#757575';

const INITIAL_SUMMARY = {
    barcode: '',
    current_status: '',
    current_location: '',
    total_transfers: 0,
    total_missing: 0,
    total_reactivated: 0,
    total_verified: 0,
    last_activity: null
};

const formatDateTime = value => (value ? dayjs(value).format('DD-MM-YYYY hh:mm A') : '-');

const BarcodeAuditHistory = () => {
    const dispatch = useDispatch();
    const classes = useStyle();
    const localClasses = useLocalStyle();

    const [barcode, setBarcode] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [eventType, setEventType] = useState(null);

    const [summary, setSummary] = useState(INITIAL_SUMMARY);
    const [history, setHistory] = useState([]);
    const [isLoader, setIsLoader] = useState(false);
    const [searched, setSearched] = useState(false);

    const isFirstRender = useRef(true);

    const fetchAuditHistory = async () => {
        if (!barcode) {
            dispatch(setSnackbar(true, 'error', 'Please enter a barcode to search'));
            return;
        }

        const params = { barcode };
        if (fromDate) params.from_date = dayjs(fromDate).format('YYYY-MM-DD');
        if (toDate) params.to_date = dayjs(toDate).format('YYYY-MM-DD');
        if (eventType) params.event_type = eventType.value;

        setIsLoader(true);
        try {
            const response = await axios.get('/v1/reports/barcode/audit_history', { params });
            setSummary(response.data.summary || INITIAL_SUMMARY);
            setHistory(response.data.history || []);
        } catch (error) {
            setSummary(INITIAL_SUMMARY);
            setHistory([]);
            const message = error?.response?.data?.errors?.[0]?.message || 'Failed to fetch barcode audit history';
            dispatch(setSnackbar(true, 'error', message));
        } finally {
            setIsLoader(false);
            setSearched(true);
        }
    };

    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            fetchAuditHistory();
        }
    };

    const clearData = () => {
        setBarcode('');
        setFromDate(null);
        setToDate(null);
        setEventType(null);
        setSummary(INITIAL_SUMMARY);
        setHistory([]);
        setSearched(false);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (barcode && searched) {
            fetchAuditHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventType]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Helmet><title>Barcode Audit History</title></Helmet>
            <Loader isLoader={isLoader} />

            <Paper style={{ padding: '20px', margin: '20px', backgroundColor: '#f9f9f9' }}>
                <Typography variant="h5" gutterBottom>
                    Barcode Audit History
                </Typography>

                <Grid container spacing={2} style={{ marginTop: '10px' }}>
                    <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1">Barcode *</Typography>
                        <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            value={barcode}
                            onChange={e => setBarcode(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Scan or enter barcode"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1">From Date</Typography>
                        <DatePicker
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            value={fromDate}
                            onChange={setFromDate}
                            maxDate={toDate || undefined}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1">To Date</Typography>
                        <DatePicker
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            value={toDate}
                            onChange={setToDate}
                            minDate={fromDate || undefined}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1">Event Type</Typography>
                        <Autocomplete
                            size="small"
                            options={EVENT_TYPE_OPTIONS}
                            value={eventType}
                            onChange={(e, newValue) => setEventType(newValue)}
                            getOptionLabel={option => option.label || ''}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            renderInput={params => <TextField {...params} placeholder="All events" />}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2} style={{ marginTop: '10px' }}>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={fetchAuditHistory}>
                            SEARCH
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant="outlined" onClick={clearData}>
                            CLEAR
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {searched && (
                <Paper style={{ padding: '20px', margin: '20px', backgroundColor: '#f9f9f9' }}>
                    <Typography variant="h6" gutterBottom>
                        Summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Barcode</Typography>
                            <Typography variant="body1">{summary.barcode || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Current Status</Typography>
                            {summary.current_status ? (
                                <Chip
                                    size="small"
                                    label={summary.current_status}
                                    sx={{
                                        backgroundColor:
                                            STATUS_COLORS[summary.current_status] || DEFAULT_EVENT_COLOR,
                                        color: '#fff'
                                    }}
                                />
                            ) : (
                                <Typography variant="body1">-</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Current Location</Typography>
                            <Typography variant="body1">{summary.current_location || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Last Activity</Typography>
                            <Typography variant="body1">{formatDateTime(summary.last_activity)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Total Transfers</Typography>
                            <Typography variant="body1">{summary.total_transfers}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Total Missing</Typography>
                            <Typography variant="body1">{summary.total_missing}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Total Reactivated</Typography>
                            <Typography variant="body1">{summary.total_reactivated}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" color="textSecondary">Total Verified</Typography>
                            <Typography variant="body1">{summary.total_verified}</Typography>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {searched && (
                <Paper style={{ padding: '20px', margin: '20px', backgroundColor: '#f9f9f9' }}>
                    <Typography variant="h6" gutterBottom>
                        Lifecycle Timeline
                    </Typography>
                    <TableContainer className={classes.tblRoot} sx={{ maxHeight: '520px', overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead className={localClasses.tblHeader}>
                                <TableRow className={classes.tablrow}>
                                    <TableCell>S.No</TableCell>
                                    <TableCell>Date &amp; Time</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Previous Location</TableCell>
                                    <TableCell>Current Location</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Reference No</TableCell>
                                    <TableCell>Reference Type</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Remarks</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            No lifecycle events found for this barcode
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    history.map((event, idx) => (
                                        <TableRow className={localClasses.tablrow1} key={`${event.event_type}-${event.event_datetime}-${idx}`}>
                                            <TableCell>{idx + 1}</TableCell>
                                            <TableCell>{formatDateTime(event.event_datetime)}</TableCell>
                                            <TableCell sx={{ minWidth: 190 }}>
                                                <Chip
                                                    size="small"
                                                    label={EVENT_TYPE_LABELS[event.event_type] || event.event_type}
                                                    sx={{
                                                        backgroundColor: EVENT_TYPE_COLORS[event.event_type] || DEFAULT_EVENT_COLOR,
                                                        color: '#fff',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{event.previous_location || '-'}</TableCell>
                                            <TableCell>{event.current_location || '-'}</TableCell>
                                            <TableCell>{event.status_after_event || '-'}</TableCell>
                                            <TableCell>{event.reference_no || '-'}</TableCell>
                                            <TableCell>{event.reference_type || '-'}</TableCell>
                                            <TableCell>{event.user_name || '-'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'normal', minWidth: 220 }}>{event.remarks || '-'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </LocalizationProvider>
    );
};

export default BarcodeAuditHistory;
